import type { MessageDescriptor } from 'react-intl';

import type { WorkHomeTaskLaunch } from '../../data';

import { objectWorkspaceMessages as messages } from './ObjectWorkspace.messages';

export type ObjectWorkspaceKind = 'patient' | 'visit';
export type ObjectWorkspaceFacet = 'work' | 'details' | 'sessions' | 'documents';

export interface ObjectWorkspacePatientReference {
  id: string;
  name: string;
  sex?: string;
  age?: number;
  sourceSessionId?: string;
  patientProfileId?: string;
}

export interface ObjectWorkspaceObject {
  id: string;
  kind: ObjectWorkspaceKind;
  name: string;
  /** Source visit used to restore patient display data from a URL-backed identity. */
  sourceSessionId?: string;
  /** Real patient-profile id when the patient route was resolved from a session. */
  patientProfileId?: string;
  /** Patient attached to an encounter. Patient panes ignore this reference. */
  patient?: ObjectWorkspacePatientReference;
}

export interface ObjectWorkspacePatientDetails {
  birthDate?: string | null;
  gender?: string | null;
  email?: string | null;
  phone?: string | null;
  pastMedicalHistory?: string | null;
  currentMedications?: string | null;
  allergies?: string | null;
  additionalNotes?: string | null;
}

export type ObjectWorkspacePatientDetailsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; details: ObjectWorkspacePatientDetails };

export interface ObjectWorkspaceAnchor {
  id: string;
  kind: 'session' | 'document';
  title: string;
  occurredAt?: string;
  /** Host-formatted date; wins over `occurredAt` when the host owns timezone rules. */
  occurredAtLabel?: string;
  detail?: string;
}

export type ObjectWorkspacePatientSessionsState =
  | { status: 'loading' }
  | { status: 'error' }
  | {
      status: 'ready';
      sessions: readonly ObjectWorkspaceAnchor[];
      /** The host has another page; the facet offers "Show more" when `onShowMore` is set. */
      hasMore?: boolean;
      isLoadingMore?: boolean;
      onShowMore?: () => void;
    };

export interface ObjectWorkspaceTask {
  id: string;
  title: string;
  /** Host-resolved source, e.g. the visit a patient task came from. */
  detail?: string;
  dueDate?: Date | null;
  inProgress: boolean;
  launch: WorkHomeTaskLaunch;
}

export type ObjectWorkspaceTasksState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; tasks: readonly ObjectWorkspaceTask[] };

export type ObjectWorkspaceDocumentsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; documents: readonly ObjectWorkspaceAnchor[] };

export interface ObjectWorkspaceAnchorSelection {
  anchor: ObjectWorkspaceAnchor;
  prompt: string;
  sequence: number;
}

/** The tasks currently staged for the next Heidi chat; empty once cleared. */
export interface ObjectWorkspaceTaskSelection {
  launches: readonly WorkHomeTaskLaunch[];
  /** Composer text for the staged tasks: their titles, comma-joined. */
  prompt: string;
  sequence: number;
}

/** A Next Steps task already linked to a Heidi chat; open that chat instead of staging. */
export interface ObjectWorkspaceProjectSelection {
  projectId: string;
  title: string;
  sequence: number;
}

export const FACETS_BY_KIND: Record<
  ObjectWorkspaceKind,
  readonly { id: ObjectWorkspaceFacet; label: MessageDescriptor }[]
> = {
  patient: [
    { id: 'work', label: messages.work },
    { id: 'details', label: messages.details },
    { id: 'sessions', label: messages.sessions },
    { id: 'documents', label: messages.documents },
  ],
  visit: [
    { id: 'work', label: messages.work },
    { id: 'documents', label: messages.documents },
  ],
};
